import styled from 'styled-components';
import { FlexBox, Stack, StackProps } from '@ardoq/layout';
import { Header3, Text } from '@ardoq/typography';
import { Children, isValidElement, ReactNode, useRef } from 'react';

export type EmptyStateProps = {
  media: ReactNode;
  title: string;
  description?: ReactNode;
  children?: ReactNode;
  size?: keyof typeof EmptyStateMediaSize;
  layout?: 'horizontal' | 'vertical';
};

const EmptyStateMediaSize = {
  small: '110px',
  large: '175px',
};

const MaxWidthContainer = styled.div`
  max-width: 500px;
`;
export const EMPTY_STATE_MAX_WIDTH_CONTAINER_SELECTOR =
  MaxWidthContainer.toString();

const MediaContainer = styled.div<{ $size: EmptyStateProps['size'] }>`
  flex-shrink: 0;
  width: ${({ $size }) =>
    $size === 'small' ? EmptyStateMediaSize.small : EmptyStateMediaSize.large};
`;

export type EmptyStateActionsProps = {
  children: ReactNode;
};

export const EmptyStateActions = (_: EmptyStateActionsProps) => {
  useRef(() => {
    throw new Error('EmptyStateActions needs to be inside <EmptyState/>');
  });
  return <></>;
};

const PrivateEmptyStateActions = ({
  children,
  align,
}: EmptyStateActionsProps & Pick<StackProps, 'align'>) => (
  <Stack gap="medium" align={align}>
    {children}
  </Stack>
);

export const EmptyState = ({
  media,
  title,
  description,
  layout = 'horizontal',
  size = 'large',
  children,
}: EmptyStateProps) => {
  const LayoutContent = layout === 'horizontal' ? FlexBox : Stack;
  return (
    <FlexBox justify={layout === 'horizontal' ? 'start' : 'center'}>
      <MaxWidthContainer>
        <LayoutContent gap="medium" align="center">
          <MediaContainer $size={size}>{media}</MediaContainer>
          <Stack
            gap="medium"
            align={layout === 'vertical' ? 'center' : 'start'}
          >
            <Stack
              gap="small"
              align={layout === 'vertical' ? 'center' : 'start'}
            >
              <Header3 align={layout === 'vertical' ? 'center' : 'left'}>
                {title}
              </Header3>
              {description && (
                <Text
                  align={layout === 'vertical' ? 'center' : 'left'}
                  variant="text2"
                  color="textModerate"
                >
                  {description}
                </Text>
              )}
            </Stack>
            {Children.toArray(children).map(child => {
              if (
                typeof child === 'object' &&
                isValidElement(child) &&
                child.type === EmptyStateActions
              ) {
                return (
                  <PrivateEmptyStateActions
                    key={child.key}
                    align={layout === 'vertical' ? 'center' : 'start'}
                    {...(child.props as EmptyStateActionsProps)}
                  />
                );
              }
              return child;
            })}
          </Stack>
        </LayoutContent>
      </MaxWidthContainer>
    </FlexBox>
  );
};
